import React, { useState } from 'react';
import { X, Check, ArrowRight, Target, Package, DollarSign, Zap } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { updateOnboardingStatus, currentCompany } = useTenant();

  // Obtener información de la empresa actual
  const isLite = currentCompany?.islite === true && !currentCompany?.plan_id;
  const maxBusinessLines = currentCompany?.business_line || 10;
  const maxWords = currentCompany?.business_word || 10;
  const versionName = isLite ? 'Versión Gratuita' : 'Versión Pro';
  const versionDescription = isLite 
    ? `Puedes crear ${maxBusinessLines} línea de negocio con máximo ${maxWords} palabras clave`
    : `Puedes crear hasta ${maxBusinessLines} líneas de negocio con máximo ${maxWords} palabras clave cada una`;

  const steps = [
    {
      title: "¡Bienvenido a Licitatrak!",
      subtitle: "Encuentra, prioriza y gana licitaciones con ayuda de la IA",
      content: (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-6">
            <Zap className="w-10 h-10 text-white" />
          </div>
          <p className="text-gray-600 text-lg leading-relaxed">
            Te ayudaremos a configurar tu cuenta paso a paso para encontrar las mejores oportunidades de licitación que se adapten a tu negocio.
          </p>
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-900 mb-1">
                  {versionName}
                </h4>
                <p className="text-sm text-blue-700">
                  {versionDescription}. 
                  {isLite && (
                    <>
                      {' '}Para más líneas y funcionalidades avanzadas,{' '}
                      <a 
                        href="https://www.licitatrak.cl" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-medium text-blue-800 hover:text-blue-900 underline"
                      >
                        puedes revisar nuestros planes en nuestro portal web
                      </a>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Paso 1: Crear Líneas de Negocio",
      subtitle: "Define las áreas de tu empresa para recibir alertas personalizadas",
      content: (
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">¿Qué son las líneas de negocio?</h3>
              <p className="text-gray-600 mb-4">
                Son las áreas específicas de tu empresa para las que quieres recibir alertas de licitaciones. 
                Por ejemplo: "Construcción", "Servicios de Limpieza", "Consultoría IT".
              </p>
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-purple-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-purple-900 mb-1">
                      🤖 Búsqueda Inteligente con IA
                    </h4>
                    <p className="text-sm text-purple-700">
                      Nuestro sistema utiliza <strong>Inteligencia Artificial</strong> para buscar las palabras clave 
                      en los títulos de las licitaciones, encontrando oportunidades que coincidan con tu negocio 
                      de manera más precisa y eficiente.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> Agrega palabras clave específicas que nuestra IA buscará en los títulos de las licitaciones para mayor precisión.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Paso 2: Crear Productos",
      subtitle: "Registra tu catálogo de productos y servicios",
      content: (
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full">
                <Package className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Catálogo de productos</h3>
              <p className="text-gray-600 mb-4">
                Registra los productos y servicios que tu empresa ofrece. Esto te permitirá cotizar y evaluar costos vs presupuestos.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>⏳ Próximo:</strong> Esta funcionalidad estará disponible después de crear tu línea de negocio.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Paso 3: Tasas de Cambio",
      subtitle: "Configura la conversión de monedas para cálculos precisos",
      content: (
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Conversión automática</h3>
              <p className="text-gray-600 mb-4">
                Configura las tasas de cambio de monedas para calcular correctamente los montos de las licitaciones 
                y tomar mejores decisiones de negocio.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>⏳ Próximo:</strong> Esta funcionalidad estará disponible después de completar los pasos anteriores.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "¡Listo para comenzar!",
      subtitle: "Ya tienes toda la información necesaria para empezar",
      content: (
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-4">
            <Check className="w-10 h-10 text-white" />
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">¡Gracias por preferirnos!</h3>
            <p className="text-gray-600 text-lg">
              Estamos aquí para ayudarte a encontrar las mejores oportunidades de licitación. 
              Si tienes alguna pregunta, no dudes en contactarnos en contacto@licitatrak.cl
            </p>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-700">
                <strong>🎯 Siguiente:</strong> Ve a "Mi Perfil" para crear tu{isLite ? ' primera' : ''} línea de negocio y comenzar a recibir alertas personalizadas.
                {!isLite && (
                  <><br /><strong>💡 Recuerda:</strong> Puedes crear hasta {maxBusinessLines} líneas de negocio con {maxWords} palabras clave cada una.</>
                )}
              </p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    if (dontShowAgain) {
      try {
        await updateOnboardingStatus('Onboarding1', true);
      } catch (error) {
        console.error('Error updating onboarding status:', error);
      }
    }
    onComplete();
  };

  const handleSkip = async () => {
    if (dontShowAgain) {
      try {
        await updateOnboardingStatus('Onboarding1', true);
      } catch (error) {
        console.error('Error updating onboarding status:', error);
      }
    }
    onComplete();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{steps[currentStep].title}</h2>
              <p className="text-blue-100 text-sm">{steps[currentStep].subtitle}</p>
            </div>
            <button
              onClick={handleSkip}
              className="text-blue-200 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 flex-1 rounded-full transition-colors ${
                    index <= currentStep ? 'bg-white' : 'bg-blue-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-blue-100 text-xs mt-2">
              Paso {currentStep + 1} de {steps.length}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {steps[currentStep].content}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="dontShowAgain"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="dontShowAgain" className="ml-2 text-sm text-gray-600">
                No mostrar más este tutorial
              </label>
            </div>
            
            <div className="flex space-x-3">
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Anterior
                </button>
              )}
              
              <button
                onClick={handleNext}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
              >
                {currentStep === steps.length - 1 ? 'Comenzar' : 'Siguiente'}
                {currentStep < steps.length - 1 && <ArrowRight className="w-4 h-4 ml-2" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
